import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTranslations } from '../context/TranslationsContext';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Clock, Repeat } from 'lucide-react';
import { getLocalizedPath } from '../utils/routes';

const API = process.env.REACT_APP_BACKEND_URL;

const DAY_KEYS = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
const MONTH_KEYS_SHORT = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
const MONTH_KEYS_FULL = ['january','february','march','april','may_full','june','july','august','september','october','november','december'];

const EventsSection = () => {
  const { i18n } = useTranslation();
  const { td } = useTranslations();
  const lang = i18n.language;
  const [events, setEvents] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/events?limit=5`),
      axios.get(`${API}/api/events`)
    ])
      .then(([limitedRes, allRes]) => {
        setEvents(limitedRes.data);
        setTotalCount(allRes.data.length);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const ls = (field, fallback = '') => {
    if (!field) return fallback;
    if (typeof field === 'string') return field;
    return field[lang] || field.pl || field.en || Object.values(field)[0] || fallback;
  };

  const getDateParts = (dateStr) => {
    const d = new Date(dateStr);
    const monthKey = MONTH_KEYS_SHORT[d.getMonth()];
    return {
      day: d.getDate(),
      month: td(`months.${monthKey}`) || d.toLocaleDateString('pl-PL', { month: 'short' }),
    };
  };

  const formatDayOfWeek = (dayIndex) => {
    const key = DAY_KEYS[dayIndex];
    return td(`days.${key}`) || key;
  };

  const getWhenText = (event) => {
    if (!event.isRecurring) {
      const d = new Date(event.nextDate || event.date);
      const dayKey = DAY_KEYS[d.getDay()];
      const monthKey = MONTH_KEYS_FULL[d.getMonth()];
      const dayName = td(`days.${dayKey}`) || dayKey;
      const monthName = td(`months.${monthKey}`) || '';
      return `${dayName}, ${d.getDate()} ${monthName}`;
    }
    const days = (event.recurringConfig?.daysOfWeek || [])
      .sort((a, b) => a - b)
      .map(d => formatDayOfWeek(d))
      .join(', ');
    return `${td('events.every') || 'Każdy'} ${days}`;
  };

  const getUntilLabel = (event) => {
    if (!event.isRecurring || !event.recurringConfig?.endDate) return '';
    const d = new Date(event.recurringConfig.endDate);
    const monthKey = MONTH_KEYS_FULL[d.getMonth()];
    const monthName = td(`months.${monthKey}`) || '';
    return `${td('events.until') || 'do'} ${d.getDate()} ${monthName}`;
  };

  const getBadgeLabel = (badge) => {
    if (typeof badge === 'string') return badge;
    return badge[lang] || badge.pl || badge.en || Object.values(badge)[0] || '';
  };

  const [fitnetTranslations, setFitnetTranslations] = useState({});
  useEffect(() => {
    axios.get(`${API}/api/admin/fitnet-translations`).then(res => setFitnetTranslations(res.data)).catch(() => {});
  }, []);

  const normalizeNameToKey = (name) => {
    if (!name) return '';
    return name.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/ł/g, 'l').replace(/Ł/g, 'L')
      .replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
  };

  const makeSlug = (name) => name.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/ł/g, 'l').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const getBookLinkForEvent = (event) => {
    const bookPath = getLocalizedPath('book');
    if (!event?.badges || event.badges.length === 0) return bookPath;
    const labels = event.badges.map(b => (typeof b === 'string' ? b : b.pl || b.en || '').toUpperCase());
    const unique = [...new Set(labels)];
    if (unique.length !== 1) return bookPath;
    const key = normalizeNameToKey(unique[0]);
    const translated = fitnetTranslations[key]?.[lang];
    if (translated) return `${bookPath}/${makeSlug(translated)}`;
    return `${bookPath}/${makeSlug(unique[0])}`;
  };

  const eventsPath = getLocalizedPath('events');

  if (loading || events.length === 0) return null;

  const [featured, ...rest] = events;
  const featuredDateParts = !featured.isRecurring ? getDateParts(featured.nextDate || featured.date) : null;
  const plainDesc = ls(featured.description)?.replace(/<[^>]+>/g, '').slice(0, 180);

  // ─── Single event: thumbnail left + details right ───
  if (events.length === 1) {
    return (
      <section className="py-20 px-5 md:px-12 max-w-7xl mx-auto" data-testid="events-section">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex justify-between items-center mb-12"
        >
          <div>
            <h2 className="text-3xl md:text-4xl font-['Syne'] font-bold mb-2">{td('events.title') || 'Wydarzenia'}</h2>
            <p className="text-muted-foreground">{td('events.subtitle') || 'Zobacz nasz kalendarz wydarzeń'}</p>
          </div>
          <Link to={eventsPath} className="hidden md:flex items-center gap-2 text-primary hover:underline font-medium" data-testid="events-view-all">
            {td('events.viewAll') || 'Zobacz wszystkie'} <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        <Link to={`${eventsPath}/${featured.slug}`} className="group block">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card overflow-hidden grid md:grid-cols-[240px_1fr] gap-0"
          >
            {/* Thumbnail 1:1 */}
            <div className="relative aspect-square overflow-hidden">
              {featured.image ? (
                <div
                  className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-700"
                  style={{ backgroundImage: `url(${featured.image.startsWith('/') ? API + featured.image : featured.image})` }}
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-purple-600/20 flex items-center justify-center">
                  <Calendar className="w-16 h-16 text-white/15" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              {featured.badges?.length > 0 && (
                <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
                  {featured.badges.map((badge, i) => (
                    <span key={i} className="rounded-full border border-primary/30 bg-primary/20 px-2.5 py-0.5 text-[11px] text-primary backdrop-blur-sm font-medium">
                      {getBadgeLabel(badge)}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="p-6 md:p-8 flex flex-col justify-center">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 group-hover:text-primary transition-colors">
                {ls(featured.title)}
              </h3>

              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400 mb-4">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-primary" />
                  {getWhenText(featured)}
                </span>
                {featured.time && (
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-primary" />
                    {featured.time}{featured.endTime ? ` - ${featured.endTime}` : ''}
                  </span>
                )}
              </div>

              {featured.isRecurring && <p className="text-sm text-gray-500 mb-3">{getUntilLabel(featured)}</p>}

              {plainDesc && (
                <p className="text-sm text-gray-400 mb-4 line-clamp-3">{plainDesc}{plainDesc.length >= 180 ? '...' : ''}</p>
              )}

              <div className="flex items-center gap-4 mt-auto">
                <span className="text-primary font-bold text-lg">
                  {featured.price === 0 || featured.price == null ? (td('events.free') || 'Bezpłatne') : `${featured.price.toFixed(2)} zł`}
                </span>
                <span className="text-sm text-gray-400 group-hover:text-primary transition-colors flex items-center gap-1">
                  {td('events.details') || 'Szczegóły'} <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </div>
          </motion.div>
        </Link>
      </section>
    );
  }

  // ─── Multiple events: featured card left + list right ───
  return (
    <section className="py-20 px-5 md:px-12 max-w-7xl mx-auto" data-testid="events-section">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="flex justify-between items-center mb-12"
      >
        <div>
          <h2 className="text-3xl md:text-4xl font-['Syne'] font-bold mb-2">{td('events.title') || 'Wydarzenia'}</h2>
          <p className="text-muted-foreground">{td('events.subtitle') || 'Zobacz nasz kalendarz wydarzeń'}</p>
        </div>
        <Link to={eventsPath} className="hidden md:flex items-center gap-2 text-primary hover:underline font-medium" data-testid="events-view-all">
          {td('events.viewAll') || 'Zobacz wszystkie'} <ArrowRight className="w-4 h-4" />
        </Link>
      </motion.div>

      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        {/* Featured event - big card */}
        <Link to={`${eventsPath}/${featured.slug}`} className="group">
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card overflow-hidden h-full flex flex-col"
          >
            <div className="relative aspect-square overflow-hidden border-b border-white/10">
              {featured.image ? (
                <div
                  className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-700"
                  style={{ backgroundImage: `url(${featured.image.startsWith('/') ? API + featured.image : featured.image})` }}
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-purple-600/20 flex items-center justify-center">
                  <Calendar className="w-16 h-16 text-white/15" />
                </div>
              )}
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.08),rgba(0,0,0,0.55))]" />
              <div className="absolute left-4 top-4 flex flex-wrap gap-2">
                {featured.badges?.map((badge, i) => (
                  <span key={i} className="rounded-full border border-primary/30 bg-primary/20 px-3 py-1 text-[11px] text-primary backdrop-blur-sm font-medium">
                    {getBadgeLabel(badge)}
                  </span>
                ))}
              </div>
              {featuredDateParts && (
                <div className="absolute left-4 bottom-4 bg-black/60 backdrop-blur-md rounded-lg px-3 py-2 text-center border border-white/10">
                  <div className="text-xl font-bold text-white leading-none">{featuredDateParts.day}</div>
                  <div className="text-[11px] text-primary uppercase font-medium mt-0.5">{featuredDateParts.month}</div>
                </div>
              )}
              {featured.isRecurring && !featuredDateParts && (
                <div className="absolute left-4 bottom-4 bg-black/60 backdrop-blur-md rounded-lg p-2.5 border border-white/10">
                  <Repeat className="w-4 h-4 text-primary" />
                </div>
              )}
            </div>

            <div className="p-5 flex-1 flex flex-col">
              <h3 className="text-[22px] font-semibold leading-tight tracking-[-0.02em] group-hover:text-primary transition-colors mb-2">
                {ls(featured.title)}
              </h3>

              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400 mb-3">
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-primary/70" />
                  {getWhenText(featured)}
                </span>
                {featured.time && (
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-primary/70" />
                    {featured.time}{featured.endTime ? ` - ${featured.endTime}` : ''}
                  </span>
                )}
              </div>

              {featured.isRecurring && <p className="text-xs text-gray-500 mb-2">{getUntilLabel(featured)}</p>}

              {plainDesc && (
                <p className="text-sm text-white/50 mb-3 line-clamp-2">{plainDesc}{plainDesc.length >= 180 ? '...' : ''}</p>
              )}

              <div className="mt-auto flex gap-2 pt-2 justify-between">
                <span className="rounded-full bg-primary px-5 py-3 text-sm font-medium text-white text-center">
                  {td('events.details') || 'Szczegóły'}
                </span>
                <Link
                  to={getBookLinkForEvent(featured)}
                  onClick={e => e.stopPropagation()}
                  className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white/78 hover:bg-white/[0.06] transition-colors"
                >
                  {td('events.bookNow') || 'Rezerwuj'}
                </Link>
              </div>
            </div>
          </motion.article>
        </Link>

        {/* Event list - right side */}
        <div className="glass-card px-4 py-2 md:px-5 md:py-3 flex flex-col">
          <div className="flex items-center justify-between border-b border-white/10 px-2 py-4">
            <h3 className="text-lg font-semibold tracking-[-0.02em]">
              {td('events.otherEvents') || 'Pozostałe wydarzenia'}
            </h3>
          </div>

          <div className="flex-1">
            {rest.map((event, index) => {
              const dateParts = !event.isRecurring ? getDateParts(event.nextDate || event.date) : null;

              return (
                <Link key={event.id} to={`${eventsPath}/${event.slug}`} className="group block">
                  <article
                    className={`flex gap-4 px-2 py-5 items-center ${
                      index !== rest.length - 1 ? 'border-b border-white/10' : ''
                    }`}
                  >
                    {/* Mini date calendar */}
                    {dateParts ? (
                      <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-white/5 border border-white/10 flex flex-col items-center justify-center">
                        <span className="text-lg font-bold text-white leading-none">{dateParts.day}</span>
                        <span className="text-[10px] text-primary uppercase font-medium mt-0.5">{dateParts.month}</span>
                      </div>
                    ) : (
                      <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                        <Repeat className="w-5 h-5 text-primary" />
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        {event.badges?.map((badge, i) => (
                          <span key={i} className="rounded-full border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[10px] text-white/60">
                            {getBadgeLabel(badge)}
                          </span>
                        ))}
                      </div>

                      <h4 className="text-base font-semibold tracking-[-0.01em] text-white group-hover:text-primary transition-colors truncate">
                        {ls(event.title)}
                      </h4>

                      <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-white/40">
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-primary/70" />
                          {getWhenText(event)}
                        </span>
                        {event.time && (
                          <span className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-primary/70" />
                            {event.time}{event.endTime ? ` - ${event.endTime}` : ''}
                          </span>
                        )}
                        {event.isRecurring && <span>{getUntilLabel(event)}</span>}
                      </div>
                    </div>

                    {/* Price + arrow */}
                    <div className="flex-shrink-0 text-right">
                      <div className="text-sm font-semibold text-primary">
                        {event.price === 0 || event.price == null ? (td('events.free') || 'Free') : `${event.price.toFixed(0)} zł`}
                      </div>
                      <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-primary transition-colors mt-1 ml-auto" />
                    </div>
                  </article>
                </Link>
              );
            })}
          </div>

          {/* Show more events link */}
          {totalCount > events.length && (
            <Link
              to={eventsPath}
              className="block border-t border-white/10 px-4 py-4 text-center text-sm text-white/50 hover:text-primary transition-colors"
            >
              + {totalCount - events.length} {totalCount - events.length === 1 
                ? (td('events.otherEvent') || 'inne wydarzenie') 
                : (td('events.otherEventsCount') || 'innych wydarzeń')} <ArrowRight className="w-3.5 h-3.5 inline ml-1" />
            </Link>
          )}
        </div>
      </div>

      {/* Mobile view all */}
      <div className="mt-8 md:hidden text-center">
        <Link to={eventsPath} className="text-primary hover:underline font-medium inline-flex items-center gap-2">
          {td('events.viewAll') || 'Zobacz wszystkie'} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
};

export default EventsSection;
